/* assets/registered-product.js */
(() => {
    // =========================================================
    // Helpers
    // =========================================================
    function qs(id) {
        return document.getElementById(id);
    }
    function qsa(sel, root = document) {
        return Array.from(root.querySelectorAll(sel));
    }
    function str(v) {
        return v == null ? "" : String(v);
    }
    function safeJsonParse(text) {
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    }
    function formatTime(isoOrDate) {
        try {
            const d = isoOrDate ? new Date(isoOrDate) : new Date();
            return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
        } catch {
            return "";
        }
    }

    // =========================================================
    // UI: Swap row actions
    // =========================================================
    // A card whose status already explains itself (claimed, pending approval,
    // inactive) renders its message and its Contact Customer Care button from
    // the server. Those must never be toggled by the parts check: the button
    // belongs to the status, not to whether a parts page exists.
    function careIsServerShown(card) {
        return (card.dataset.careShown || "").trim() === "true";
    }

    function swapRowToContact(card) {
        if (!card) return;

        const view = card.querySelector(".wss-view-parts");
        const drawer = card.querySelector(".wss-unavailable-drawer");
        const care = card.querySelector(".wss-side-care");

        // Parts page missing for this model: hide View Parts, reveal the
        // message drawer, and surface Contact Customer Care in the panel's
        // care slot (same placement as on a claimed card).
        if (view) view.style.display = "none";
        if (drawer) drawer.style.display = "flex";
        if (care && !careIsServerShown(card)) care.style.display = "";
    }

    function keepRowAsViewParts(card) {
        if (!card) return;

        const view = card.querySelector(".wss-view-parts");
        const drawer = card.querySelector(".wss-unavailable-drawer");
        const care = card.querySelector(".wss-side-care");

        if (view) view.style.display = "";
        if (drawer) drawer.style.display = "none";
        if (care && !careIsServerShown(card)) care.style.display = "none";
    }

    // =========================================================
    // Normalize URL (fix encoded/double pages)
    // =========================================================
    function normalizePartsUrl(rawUrl) {
        const s = String(rawUrl || "").trim();
        if (!s) return "";

        // If whole path was encoded, decode it.
        if (s.includes("%2Fpages%2F") || s.includes("%2Fpages%2Fbom-product%2F")) {
            try {
                const decoded = decodeURIComponent(s);
                return decoded
                    .replace("/pages//pages/", "/pages/")
                    .replace("//pages/", "/pages/");
            } catch {
                return s;
            }
        }

        return s;
    }

    // =========================================================
    // SKU existence cache (1 week)
    // =========================================================
    const SKU_CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

    // v2: v1 entries could hold a cached "false" written on a transient
    // proxy error; the new namespace orphans them all.
    function cacheKey(shop, sku) {
        return `wss_parts_exists_v2:${shop || "unknown"}:${sku}`;
    }

    function cacheGet(shop, sku) {
        try {
            const raw = localStorage.getItem(cacheKey(shop, sku));
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object") return null;

            if (Date.now() > parsed.expiresAt) {
                localStorage.removeItem(cacheKey(shop, sku));
                return null;
            }

            return parsed.value === 1 ? true : parsed.value === 0 ? false : null;
        } catch {
            return null;
        }
    }

    function cacheSet(shop, sku, exists) {
        try {
            localStorage.setItem(
                cacheKey(shop, sku),
                JSON.stringify({
                    value: exists ? 1 : 0,
                    expiresAt: Date.now() + SKU_CACHE_TTL,
                })
            );
        } catch {
            // ignore
        }
    }

    // =========================================================
    // Check SKU existence (uses your product-search endpoint)
    // =========================================================
    async function checkSkuExists({ sku, shop, searchEndpoint }) {
        const s = String(sku || "").trim();
        if (!s || s.length < 2) return false;

        // 1) cache
        const cached = cacheGet(shop, s);
        if (cached !== null) return cached;

        // 2) API call
        const params = new URLSearchParams();
        params.set("q", s);
        params.set("limit", "1");
        params.set("by", "sku");
        if (shop) params.set("shop_domain", shop);

        const url = `${searchEndpoint}?${params.toString()}`;

        try {
            const res = await fetch(url, {
                method: "GET",
                headers: { Accept: "application/json" },
                credentials: "same-origin",
            });

            const txt = await res.text().catch(() => "");
            const json = safeJsonParse(txt);

            // Only a real answer from the proxy is worth acting on (or
            // caching). A 401/500/non-JSON blip is "unknown", not "missing":
            // caching false here used to brand the SKU unavailable for a week.
            if (!res.ok || !json || json.ok === false) {
                return null;
            }

            const exists = Number(json.count || 0) > 0;
            cacheSet(shop, s, exists);
            return exists;
        } catch {
            // network error: unknown, no cache write
            return null;
        }
    }

    // =========================================================
    // Optional: Validate BOM page exists (404 or fake-200 detection)
    // =========================================================
    async function validateBomPage({ url, marker }) {
        const u = normalizePartsUrl(url);
        if (!u) return false;

        try {
            // HEAD is faster but Shopify can behave differently per theme.
            // Use GET to be safest.
            const res = await fetch(u, {
                method: "GET",
                credentials: "same-origin",
                headers: { Accept: "text/html" },
            });

            if (res.status === 404) return false;
            if (!res.ok) return false;

            // Optional marker check (detect “fake 200 not found”)
            if (marker) {
                const html = await res.text().catch(() => "");
                const low = (html || "").toLowerCase();

                const looksMissing =
                    !html ||
                    low.includes("page not found") ||
                    low.includes("not found") ||
                    low.includes("404") ||
                    !html.includes(marker);

                if (looksMissing) return false;
            }

            return true;
        } catch {
            // network error: unknown, not proof the page is missing
            return null;
        }
    }

    // =========================================================
    // Validate all “View Parts” links
    // Strategy:
    // 1) Determine SKU from the URL last segment
    // 2) Check SKU existence via product-search endpoint
    // 3) If exists -> optionally verify BOM page URL returns real content
    // =========================================================
    async function validatePartsLinks(rootEl) {
        const root = rootEl || document;
        const links = qsa(".wss-view-parts[data-url]", root);
        if (!links.length) return;

        const shop = rootEl?.dataset?.shop || rootEl?.dataset?.shopDomain || "";
        const searchEndpoint = rootEl?.dataset?.productSearchEndpoint || "/apps/wss/product-search";
        const marker =
            rootEl?.dataset && typeof rootEl.dataset.partsMarker === "string"
                ? rootEl.dataset.partsMarker.trim()
                : "";

        const MAX = 4;
        let idx = 0;

        async function worker() {
            while (idx < links.length) {
                const i = idx++;
                const link = links[i];

                const rawUrl = link.dataset.url || link.getAttribute("href") || "";
                const url = normalizePartsUrl(rawUrl);

                // The registration card, not the action column: for a claimed grill
                // the View Parts / Contact buttons live in the full-width drawer
                // below the row, so scope the swap/keep helpers to the whole card.
                const actionBox = link.closest(".wss-reg-card");
                if (!actionBox) continue;

                // Default: keep View Parts visible until proven invalid
                keepRowAsViewParts(actionBox);

                // Prefer the server-rendered model. The href may carry
                // registration query params for WSS cart-line attribution.
                const last = (url.split("/").pop() || "").split("?")[0].split("#")[0].trim();
                const sku = (link.dataset.sku || decodeURIComponent(last || "")).trim();

                if (!sku) {
                    swapRowToContact(actionBox);
                    continue;
                }

                // 1) SKU exists? Swap only on an authoritative "no";
                //    null means the check itself failed, so fail open and
                //    let the BOM fetch have its own say.
                const skuExists = await checkSkuExists({ sku, shop, searchEndpoint });
                if (skuExists === false) {
                    swapRowToContact(actionBox);
                    continue;
                }

                // 2) BOM page exists? Same rule: a 404 swaps, an unreachable
                //    page does not.
                const bomOk = await validateBomPage({ url, marker });
                if (bomOk === false) {
                    swapRowToContact(actionBox);
                    continue;
                }

                // Valid -> keep as View Parts
                keepRowAsViewParts(actionBox);
            }
        }

        const workers = [];
        for (let w = 0; w < Math.min(MAX, links.length); w++) workers.push(worker());
        await Promise.all(workers);
    }

    // =========================================================
    // Warranty Refresh UI
    // =========================================================
    // Client-side pagination over the server-rendered registration rows.
    // Controls are only added when the list exceeds one page.
    function initPagination(root) {
        const grid = document.querySelector(".wss-grid");
        if (!grid) return;

        const existing = document.querySelector(".wss-pagination");
        if (existing) existing.remove();

        // Paginate whole cards, not just .wss-row: a claimed card has a drawer
        // sibling, and hiding only the row left orphan drawers on hidden pages.
        const rows = qsa(".wss-reg-card", grid);
        const pageSize = Math.max(1, parseInt(root.dataset.pageSize || "10", 10) || 10);
        if (rows.length <= pageSize) return;

        const totalPages = Math.ceil(rows.length / pageSize);
        let current = 1;

        const nav = document.createElement("div");
        nav.className = "wss-pagination";

        const prev = document.createElement("button");
        prev.type = "button";
        prev.className = "wss-btn wss-btn--secondary wss-page-prev";
        // Arrow only; the label lives in aria-label so the control still
        // announces itself.
        prev.setAttribute("aria-label", "Previous page");
        prev.innerHTML =
            '<svg class="wss-page-arrow" viewBox="0 0 24 24" width="18" height="18" ' +
            'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
            'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
            '<path d="M15 5l-7 7 7 7"/></svg>';

        const jump = document.createElement("input");
        jump.type = "number";
        jump.min = "1";
        jump.max = String(totalPages);
        jump.className = "wss-page-jump";
        jump.setAttribute("aria-label", "Go to page");

        const label = document.createElement("span");
        label.className = "wss-page-label";
        label.textContent = `of ${totalPages}`;

        const next = document.createElement("button");
        next.type = "button";
        next.className = "wss-btn wss-btn--secondary wss-page-next";
        next.setAttribute("aria-label", "Next page");
        next.innerHTML =
            '<svg class="wss-page-arrow" viewBox="0 0 24 24" width="18" height="18" ' +
            'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
            'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
            '<path d="M9 5l7 7-7 7"/></svg>';

        nav.appendChild(prev);
        nav.appendChild(jump);
        nav.appendChild(label);
        nav.appendChild(next);
        grid.parentNode.insertBefore(nav, grid.nextSibling);

        function render() {
            const start = (current - 1) * pageSize;
            const end = start + pageSize;
            rows.forEach((row, i) => {
                row.style.display = i >= start && i < end ? "" : "none";
            });
            jump.value = String(current);
            prev.disabled = current === 1;
            next.disabled = current === totalPages;
        }

        function goTo(page) {
            current = Math.min(Math.max(1, page), totalPages);
            render();
            grid.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        function jumpTo() {
            const page = parseInt(jump.value, 10);
            goTo(Number.isFinite(page) ? page : current);
        }

        prev.addEventListener("click", () => goTo(current - 1));
        next.addEventListener("click", () => goTo(current + 1));
        jump.addEventListener("change", jumpTo);
        jump.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                jumpTo();
            }
        });

        render();
    }

    function initWss() {
        const root = qs("wss-root");
        if (!root) return;

        if (root.dataset.initialized === "true") return;
        root.dataset.initialized = "true";

        initPagination(root);

        const baseEndpoint = root.dataset.endpoint || "";
        const email = root.dataset.email || "";
        const sessionKey = root.dataset.sessionKey || "wss_auto_refreshed";

        if (!baseEndpoint || !email) return;

        const endpoint = `${baseEndpoint}?email=${encodeURIComponent(email)}`;

        const statusEl = qs("wss-status");
        const statusTextEl = qs("wss-status-text");
        const refreshBtn = qs("wss-refresh");

        if (!statusEl || !statusTextEl || !refreshBtn) return;

        function setState(className, text) {
            statusEl.classList.remove("is-loading", "is-success", "is-error");
            if (className) statusEl.classList.add(className);
            statusTextEl.textContent = text;
        }

        function disableRefresh(disabled) {
            refreshBtn.disabled = !!disabled;
        }

        async function callApi({ reloadOnSuccess = false, markSession = false } = {}) {
            setState("is-loading", "Refreshing warranty data…");
            disableRefresh(true);

            try {
                const res = await fetch(endpoint, {
                    headers: { Accept: "application/json" },
                    credentials: "same-origin",
                });

                const bodyText = await res.text().catch(() => "");
                const payload = safeJsonParse(bodyText);

                if (!res.ok) {
                    setState("is-error", `Request failed (${res.status}). Please try again.`);
                    disableRefresh(false);
                    return;
                }

                if (!payload) {
                    setState("is-error", "Unexpected response (not JSON).");
                    disableRefresh(false);
                    return;
                }

                if (payload.success) {
                    const timeLabel = payload.refreshedAt ? formatTime(payload.refreshedAt) : formatTime(new Date());
                    setState("is-success", `Warranty data updated at ${timeLabel}`);
                    disableRefresh(false);

                    if (markSession) sessionStorage.setItem(sessionKey, String(Date.now()));
                    if (reloadOnSuccess) setTimeout(() => window.location.reload(), 650);
                    return;
                }

                setState("is-error", payload.message || "Couldn’t refresh. Please try again.");
                disableRefresh(false);
            } catch {
                setState("is-error", "Network error. Please try again.");
                disableRefresh(false);
            }
        }

        refreshBtn.addEventListener("click", () => {
            sessionStorage.removeItem(sessionKey);
            callApi({ reloadOnSuccess: true, markSession: true });
        });

        // Auto-sync when never synced this session or the last sync is older
        // than the TTL, so revoked approvals / new claims surface without a
        // manual refresh. (Legacy "done" markers parse as stale.)
        const SYNC_TTL_MS = 30 * 60 * 1000;
        const lastSynced = Number(sessionStorage.getItem(sessionKey));
        const isStale = !Number.isFinite(lastSynced) || Date.now() - lastSynced > SYNC_TTL_MS;

        if (isStale) {
            callApi({ reloadOnSuccess: true, markSession: true });
        } else {
            setState("", "Warranty data is up to date. You can refresh anytime.");
            disableRefresh(false);
        }

        // Validate parts links after render (does not block UI)
        validatePartsLinks(root).catch(() => {});
    }

    // Normal load
    document.addEventListener("DOMContentLoaded", initWss);

    // Theme editor section reload support
    document.addEventListener("shopify:section:load", () => {
        const root = qs("wss-root");
        if (root) root.dataset.initialized = "false";
        initWss();
    });
})();
